import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, MessageCircle, Dumbbell, TrendingUp, ArrowRight, Lock, RefreshCw, UserX, Apple, CalendarPlus, Wand2 } from "lucide-react";
import AIInsights from "../components/AIInsights";
import CreatePlanShare from "../components/CreatePlanShare";
import { isAIEnabled, planLockReason } from "../lib/plan";

/**
 * AI Coach - assistant-style entry point. Each card is a *capability* the
 * coach can run on the right context (a lead, a member, the whole gym).
 * Design intent: feel like Notion AI / Linear AI, not a form to fill in.
 */
const ASSISTANTS = [
  {
    key: "follow-up",
    icon: MessageCircle,
    title: "Generate follow-up",
    blurb: "Write the next WhatsApp message for any lead - Hindi, English, or Hinglish.",
    tone: "leads",
    to: "/leads",
    cta: "Pick a lead",
  },
  {
    key: "lost-lead",
    icon: UserX,
    title: "Recover a lost lead",
    blurb: "Craft a re-engagement message for someone who went cold or dropped off.",
    tone: "risk",
    to: "/leads?status=lost",
    cta: "See lost leads",
  },
  {
    key: "renewal",
    icon: RefreshCw,
    title: "Renew members",
    blurb: "Auto-draft renewal nudges for members whose plan expires in the next 14 days.",
    tone: "growth",
    to: "/members?tab=expiring",
    cta: "Open expiring",
  },
  {
    key: "workout",
    icon: Dumbbell,
    title: "Workout plan",
    blurb: "Personalised plan: strength, hypertrophy, boxing, Muay Thai, MMA, BJJ, calisthenics.",
    tone: "ai",
    to: "/members",
    cta: "Pick a member",
  },
  {
    key: "diet",
    icon: Apple,
    title: "Diet plan",
    blurb: "Macro-split meal plan tailored to a member's goal and dietary preference.",
    tone: "growth",
    to: "/members",
    cta: "Pick a member",
  },
  {
    key: "event",
    icon: CalendarPlus,
    title: "Event planner",
    blurb: "Plan a fitness challenge, fight night or anniversary event end-to-end.",
    tone: "trials",
    to: "/calendar",
    cta: "Open calendar",
  },
];

const TONE_BG = {
  growth: "bg-growth/10 text-growth",
  leads:  "bg-leads/10  text-leads",
  trials: "bg-trials/10 text-trials",
  tasks:  "bg-tasks/10  text-tasks",
  ai:     "bg-ai/10     text-ai",
  risk:   "bg-risk/10   text-risk",
  ops:    "bg-ops/10    text-ops",
};

export default function AIPage() {
  const aiEnabled = isAIEnabled();
  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10 max-w-6xl mx-auto">
      <header className="mb-8 lg:mb-10">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-ai mb-2">AI Coach</p>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-foreground flex items-center gap-2.5" data-testid="ai-page-title">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-ai/10 text-ai">
            <Wand2 className="w-5 h-5" strokeWidth={2.4} />
          </span>
          Your AI Coach
          <span className="text-[9px] font-bold uppercase tracking-wider bg-foreground text-background px-1.5 py-0.5 rounded ml-2">beta</span>
        </h1>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">
          Ask the coach to do something. It picks the right data, writes the message, and gets it ready to send.
        </p>
      </header>

      {!aiEnabled && (
        <div className="rounded-2xl border border-dashed border-ai/30 p-8 mb-6 text-center" data-testid="ai-page-locked">
          <Lock className="w-7 h-7 mx-auto mb-2.5 text-ai" />
          <p className="text-sm font-semibold text-foreground mb-1">AI features locked</p>
          <p className="text-xs text-muted-foreground max-w-md mx-auto">{planLockReason()}</p>
        </div>
      )}

      {aiEnabled && (
        <div className="mb-8">
          <AIInsights />
        </div>
      )}

      {/* Assistant cards */}
      <div className="mb-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70 mb-3">Capabilities</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ai-assistants">
          {ASSISTANTS.map(a => (
            <Link
              key={a.key}
              to={a.to}
              data-testid={`ai-assistant-${a.key}`}
              className="group rounded-2xl border border-border bg-card p-5 hover:border-foreground/15 hover:shadow-sm transition-all block"
            >
              <span className={`inline-flex items-center justify-center w-10 h-10 rounded-xl mb-4 ${TONE_BG[a.tone]}`}>
                <a.icon className="w-4.5 h-4.5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display text-[15px] font-bold text-foreground mb-1.5 tracking-tight">{a.title}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed mb-4">{a.blurb}</p>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground/80 group-hover:text-foreground group-hover:gap-2 transition-all">
                {a.cta} <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          ))}
        </div>
      </div>

      {aiEnabled && (
        <div className="mb-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70 mb-3">Quick generate</p>
          <CreatePlanShare />
        </div>
      )}

      <div className="rounded-2xl border border-border p-5 bg-card">
        <h3 className="font-display text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-ai" /> What the coach can do today
        </h3>
        <ul className="text-xs text-muted-foreground space-y-1.5 list-disc pl-5 marker:text-ai/60">
          <li>Generate Hinglish, English, हिंदी WhatsApp messages with tone control (Friendly, Urgent, Professional).</li>
          <li>Score leads as Hot, Warm, Cold (heuristic, no token cost).</li>
          <li>Predict trial conversion likelihood and member drop-off risk.</li>
          <li>Build personalised workout and diet plans across strength, MMA, boxing, kickboxing, Muay Thai, BJJ, calisthenics.</li>
          <li>Surface plain-English business insights from your live KPIs.</li>
        </ul>
      </div>
    </div>
  );
}
